<template>
  <div class="satisfaction-container">
    <div class="section-header">
      <div class="header-left">
        <h2 class="title">服务满意度评价</h2>
        <span class="sub-title">采购交付履约、原厂技术支持与供应链响应效率评价反馈</span>
      </div>
      <el-button type="primary" icon="el-icon-edit-outline" class="btn-create" @click="handleAdd()">
        新增满意度评价
      </el-button>
    </div>

    <!-- 骨架屏 -->
    <div v-if="loading" class="eval-skeleton-list">
      <div v-for="i in 2" :key="i" class="sk-eval-card">
        <div class="sk-bar w-30"></div>
        <div class="sk-bar w-80"></div>
        <div class="sk-bar w-50"></div>
      </div>
    </div>

    <!-- 评价卡片列表（单列自适应宽屏布局） -->
    <div class="eval-list" v-else-if="data && data.length > 0">
      <div class="eval-card" v-for="(item, index) in data" :key="index">
        <div class="card-header">
          <div class="header-badge">
            <i class="el-icon-document-checked"></i>
            <span>履约满意度评价记录 #{{ index + 1 }}</span>
          </div>
          <div class="card-actions">
            <span class="status-tag">
              <i class="el-icon-circle-check"></i>
              <span>已归档存证</span>
            </span>
            <el-button
              type="text"
              class="detail-button"
              @click="openDetail(item, index)"
            >
              查看详情
              <i class="el-icon-arrow-right"></i>
            </el-button>
          </div>
        </div>

        <div class="card-body">
          <div
            class="qa-item"
            v-for="(field, idx) in field_list_view.slice(0, 4)"
            :key="idx"
          >
            <!-- 问卷题目序号与完整题干 -->
            <div class="question-row">
              <span class="q-badge">指标 {{ idx + 1 }}</span>
              <h4 class="q-title">{{ field.field_txt }}</h4>
            </div>

            <!-- 问卷答案 / 反馈内容 -->
            <div class="answer-row">
              <!-- 单选满意度胶囊展示 -->
              <template v-if="isSatisfactionChoice(getAnswerLabel(field, item[field.field_name]))">
                <div
                  class="choice-pill"
                  :class="getChoiceClass(getAnswerLabel(field, item[field.field_name]))"
                >
                  <i class="el-icon-success" v-if="isPositive(getAnswerLabel(field, item[field.field_name]))"></i>
                  <i class="el-icon-info" v-else-if="isNeutral(getAnswerLabel(field, item[field.field_name]))"></i>
                  <i class="el-icon-warning" v-else></i>
                  <span>{{ getAnswerLabel(field, item[field.field_name]) }}</span>
                </div>
              </template>

              <!-- 意见建议/输入文本框展示 -->
              <template v-else-if="field.field_txt.includes('建议') || field.field_type === 'textarea' || field.field_type === 'input'">
                <div class="suggestion-panel">
                  <div class="panel-icon"><i class="el-icon-chat-dot-square"></i></div>
                  <div class="panel-content">
                    <p class="suggestion-text">
                      {{ item[field.field_name] && item[field.field_name] !== '- -' ? item[field.field_name] : '暂无补充意见与改进建议' }}
                    </p>
                  </div>
                </div>
              </template>

              <!-- 其他常规字段展示 -->
              <template v-else>
                <span class="plain-text">{{ getAnswerLabel(field, item[field.field_name]) }}</span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="detailVisible"
      :title="`履约满意度评价记录 #${detailRecordIndex + 1}`"
      width="760px"
      class="satisfaction-detail-dialog"
      append-to-body
    >
      <div v-if="selectedRecord" class="detail-list">
        <div
          v-for="(field, idx) in field_list_view"
          :key="field.field_name || idx"
          class="qa-item"
        >
          <div class="question-row">
            <span class="q-badge">指标 {{ idx + 1 }}</span>
            <h4 class="q-title">{{ field.field_txt }}</h4>
          </div>

          <div class="answer-row">
            <template v-if="isSatisfactionChoice(getAnswerLabel(field, selectedRecord[field.field_name]))">
              <div
                class="choice-pill"
                :class="getChoiceClass(getAnswerLabel(field, selectedRecord[field.field_name]))"
              >
                <i class="el-icon-success" v-if="isPositive(getAnswerLabel(field, selectedRecord[field.field_name]))"></i>
                <i class="el-icon-info" v-else-if="isNeutral(getAnswerLabel(field, selectedRecord[field.field_name]))"></i>
                <i class="el-icon-warning" v-else></i>
                <span>{{ getAnswerLabel(field, selectedRecord[field.field_name]) }}</span>
              </div>
            </template>

            <template v-else-if="field.field_txt.includes('建议') || field.field_type === 'textarea' || field.field_type === 'input'">
              <div class="suggestion-panel">
                <div class="panel-icon"><i class="el-icon-chat-dot-square"></i></div>
                <div class="panel-content">
                  <p class="suggestion-text">
                    {{ selectedRecord[field.field_name] && selectedRecord[field.field_name] !== '- -' ? selectedRecord[field.field_name] : '暂无补充意见与改进建议' }}
                  </p>
                </div>
              </div>
            </template>

            <template v-else>
              <span class="plain-text">{{ getAnswerLabel(field, selectedRecord[field.field_name]) }}</span>
            </template>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button type="primary" @click="detailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 规范空状态 -->
    <div class="empty-gov-state" v-if="!loading && (!data || data.length === 0)">
      <div class="icon-circle">
        <i class="el-icon-star-off"></i>
      </div>
      <p class="empty-title">暂无满意度评价记录</p>
      <p class="empty-sub">您的每一个真实评价都是我们持续提升政企采购供应链履约质量的宝贵动力</p>
    </div>
  </div>
</template>

<script>
import { structure, online } from '@/api/platform';

export default {
  name: "Satisfaction",
  data () {
    return {
      data: [],
      field_list_view: [],
      loading: true,
      detailVisible: false,
      selectedRecord: null,
      detailRecordIndex: 0,
    };
  },
  computed: {
    user () {
      return localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {};
    }
  },
  mounted () {
    this.initData();
  },
  methods: {
    async initData () {
      this.loading = true;
      try {
        await Promise.all([this.online(), this.structure()]);
      } catch (e) {
        console.error("加载满意度数据异常:", e);
      } finally {
        this.loading = false;
      }
    },
    async online () {
      const { result } = await online({
        form_head_uuid: '6cc861f99a644963a1fb694b63be2c14',
        needpage: false,
        page: 1,
        limit: 1000,
        eg9dwtvttwtizeoe: this.user.id + ''
      });
      this.data = Array.isArray(result) ? result : [];
    },
    async structure () {
      const { result } = await structure('6cc861f99a644963a1fb694b63be2c14');
      if (result && Array.isArray(result.field_list_view)) {
        this.field_list_view = result.field_list_view.filter(
          (item) => item.field_type !== 'remark' && (!item.field_extend_json || !item.field_extend_json.hidden)
        );
      }
    },
    handleAdd () {
      window.open(`https://paas.ontuotu.com/#/270ab0c29e5411ee9b8b0242321230b4/preview/6cc861f99a644963a1fb694b63be2c14?eg9dwtvttwtizeoe=${this.user.id}`, '_blank');
    },
    openDetail (record, index) {
      this.selectedRecord = record;
      this.detailRecordIndex = index;
      this.detailVisible = true;
    },
    // 将选项 UUID 映射为真实人类可读的选项标签（如“满意”）
    getAnswerLabel (field, val) {
      if (!val || val === '- -') return '- -';
      let extend = field.field_extend_json;
      if (typeof extend === 'string') {
        try {
          extend = JSON.parse(extend);
        } catch (e) {
          extend = {};
        }
      }
      if (extend) {
        const list = extend.option_list || extend.options || [];
        if (Array.isArray(list)) {
          const match = list.find((opt) => opt.id === val || opt.value === val);
          if (match) {
            return match.label || match.title || match.text || match.value || val;
          }
        }
      }
      return val;
    },
    isSatisfactionChoice (label) {
      const positive = ['满意', '很满意', '非常满意', '较满意', '比较满意'];
      const neutral = ['一般', '基本满意'];
      const negative = ['不满意', '很不满意', '不太满意'];
      return positive.concat(neutral, negative).includes(label);
    },
    isPositive (label) {
      return ['满意', '很满意', '非常满意', '较满意', '比较满意'].includes(label);
    },
    isNeutral (label) {
      return ['一般', '基本满意'].includes(label);
    },
    getChoiceClass (label) {
      if (this.isPositive(label)) return 'pill-positive';
      if (this.isNeutral(label)) return 'pill-neutral';
      return 'pill-negative';
    }
  },
};
</script>

<style lang="less" scoped>
@import '../../assets/css/var.less';

.satisfaction-container {
  width: 100%;

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 16px;
    margin-bottom: 20px;
    border-bottom: 1px solid #f1f5f9;

    .header-left {
      display: flex;
      align-items: baseline;
      gap: 12px;

      .title {
        font-size: 16px;
        font-weight: 700;
        color: #0f172a;
        margin: 0;
      }

      .sub-title {
        font-size: 13px;
        color: #64748b;
      }
    }

    .btn-create {
      background-color: @brandPrimary;
      border-color: @brandPrimary;
      font-weight: 600;
      padding: 9px 18px;
      border-radius: 6px;

      &:hover {
        background-color: @brandSecondary;
        border-color: @brandSecondary;
      }
    }
  }

  /* 骨架屏 */
  .eval-skeleton-list {
    display: flex;
    flex-direction: column;
    gap: 16px;

    .sk-eval-card {
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 22px 24px;
      background: #fff;
      display: flex;
      flex-direction: column;
      gap: 14px;

      .sk-bar {
        height: 16px;
        border-radius: 4px;
        background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
        background-size: 200% 100%;
        animation: sk-shimmer 1.5s infinite;

        &.w-30 { width: 30%; }
        &.w-50 { width: 50%; }
        &.w-80 { width: 80%; }
      }
    }
  }

  @keyframes sk-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* 评价卡片单列全宽列表 */
  .eval-list {
    display: flex;
    flex-direction: column;
    gap: 18px;
    width: 100%;

    .eval-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 20px 24px;
      transition: all 0.25s ease;

      &:hover {
        border-color: #cbd5e1;
        box-shadow: 0 4px 16px rgba(0, 60, 144, 0.06);
      }

      .card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 18px;
        padding-bottom: 14px;
        border-bottom: 1px solid #f1f5f9;

        .header-badge {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          font-weight: 700;
          color: #0f172a;

          i {
            color: @brandSecondary;
            font-size: 16px;
          }
        }

        .status-tag {
          font-size: 12px;
          color: #059669;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          padding: 3px 10px;
          border-radius: 12px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .card-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .detail-button {
          padding: 4px 0;
          font-size: 13px;
          font-weight: 600;

          i {
            margin-left: 2px;
          }
        }
      }

      .card-body {
        display: flex;
        flex-direction: column;
        gap: 18px;

        .qa-item {
          display: flex;
          flex-direction: column;
          gap: 10px;

          .question-row {
            display: flex;
            align-items: baseline;
            gap: 10px;

            .q-badge {
              font-size: 11px;
              font-weight: 700;
              color: @brandSecondary;
              background: #eff6ff;
              border: 1px solid #dbeafe;
              padding: 2px 8px;
              border-radius: 4px;
              white-space: nowrap;
              flex-shrink: 0;
            }

            .q-title {
              font-size: 13.5px;
              font-weight: 600;
              color: #1e293b;
              line-height: 1.6;
              margin: 0;
            }
          }

          .answer-row {
            padding-left: 2px;

            .choice-pill {
              display: inline-flex;
              align-items: center;
              gap: 6px;
              padding: 6px 16px;
              border-radius: 6px;
              font-size: 13px;
              font-weight: 600;

              &.pill-positive {
                background: #f0fdf4;
                color: #16a34a;
                border: 1px solid #bbf7d0;

                i {
                  font-size: 14px;
                  color: #16a34a;
                }
              }

              &.pill-neutral {
                background: #fefce8;
                color: #ca8a04;
                border: 1px solid #fef08a;

                i {
                  font-size: 14px;
                  color: #ca8a04;
                }
              }

              &.pill-negative {
                background: #fef2f2;
                color: #dc2626;
                border: 1px solid #fecaca;

                i {
                  font-size: 14px;
                  color: #dc2626;
                }
              }
            }

            .suggestion-panel {
              display: flex;
              align-items: flex-start;
              gap: 10px;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 12px 16px;

              .panel-icon {
                color: #94a3b8;
                font-size: 16px;
                margin-top: 2px;
              }

              .panel-content {
                flex: 1;

                .suggestion-text {
                  font-size: 13px;
                  color: #475569;
                  line-height: 1.6;
                  margin: 0;
                }
              }
            }

            .plain-text {
              font-size: 13px;
              color: #334155;
              font-weight: 500;
            }
          }
        }
      }
    }
  }

  .detail-list {
    display: flex;
    flex-direction: column;
    gap: 18px;
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 8px;

    .qa-item {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .question-row {
      display: flex;
      align-items: baseline;
      gap: 10px;

      .q-badge {
        font-size: 11px;
        font-weight: 700;
        color: @brandSecondary;
        background: #eff6ff;
        border: 1px solid #dbeafe;
        padding: 2px 8px;
        border-radius: 4px;
        white-space: nowrap;
        flex-shrink: 0;
      }

      .q-title {
        font-size: 13.5px;
        font-weight: 600;
        color: #1e293b;
        line-height: 1.6;
        margin: 0;
      }
    }

    .answer-row {
      padding-left: 2px;

      .choice-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 6px 16px;
        border-radius: 6px;
        font-size: 13px;
        font-weight: 600;

        &.pill-positive {
          background: #f0fdf4;
          color: #16a34a;
          border: 1px solid #bbf7d0;
        }

        &.pill-neutral {
          background: #fefce8;
          color: #ca8a04;
          border: 1px solid #fef08a;
        }

        &.pill-negative {
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        }
      }

      .suggestion-panel {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        padding: 12px 16px;

        .panel-icon {
          color: #94a3b8;
          font-size: 16px;
          margin-top: 2px;
        }

        .panel-content {
          flex: 1;
        }

        .suggestion-text {
          font-size: 13px;
          color: #475569;
          line-height: 1.6;
          margin: 0;
        }
      }

      .plain-text {
        font-size: 13px;
        color: #334155;
        font-weight: 500;
      }
    }
  }

  /* 空状态 */
  .empty-gov-state {
    padding: 60px 0;
    text-align: center;
    background: #ffffff;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;

    .icon-circle {
      width: 60px;
      height: 60px;
      margin: 0 auto 14px;
      border-radius: 50%;
      background: #eff6ff;
      display: flex;
      align-items: center;
      justify-content: center;

      i {
        font-size: 28px;
        color: @brandPrimary;
      }
    }

    .empty-title {
      font-size: 15px;
      font-weight: 600;
      color: #334155;
      margin: 0 0 6px;
    }

    .empty-sub {
      font-size: 13px;
      color: #94a3b8;
      margin: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/client/Satisfaction.vue